import React from 'react';
import PropTypes from 'prop-types';
import { InfoCircle } from 'tabler-icons-react';
import { Anchor } from '@mantine/core';
import dayjs from 'dayjs';
import {
  currencyFormat,
  formatUtcDate,
  singularPluralFormat
} from '../../../helpers/format';
import {
  calculateExpectedProviderServiceFeeInCents,
  hasComparableServiceFees
} from '../../../helpers/paymentHelper';
import PaymentSummaryInfoCard from '../../../experimental/components/content/payments/PaymentSummaryInfoCard';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { PAYMENT_TYPE_ENUM } from '../../../config/constants';

const RegCartPaymentSummaryInfoCard = ({
  regCart,
  translatedOrder,
  actionMenu
}) => {
  const {
    regFormSubmissions: regFormSubmissionData,
    ecomOrder: translatedEcomOrder,
    invoices,
    invoiceTotals,
    pendingInstallments
  } = translatedOrder;
  const { ecomOrderProducts: ecomOrderProductData } = translatedEcomOrder;

  const showSubtotal =
    regCart.cart.discountTotalInCents > 0 ||
    (ecomOrderProductData && ecomOrderProductData.length > 0);

  const { reimbursement = [] } =
    regCart.ecomOrder?.ecomOrderPayments
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .reduce(
        (r, c) => {
          if (
            c.fkRegFormSubmissionPaymentType.toString() ===
            PAYMENT_TYPE_ENUM.REIMBURSEMENT.toString()
          ) {
            r.reimbursement.push(c);
          }
          return r;
        },
        { reimbursement: [] }
      ) || {};

  const reimbursementAmount = reimbursement.reduce((r, c) => r + c.amount, 0);

  const vendorPayout =
    ecomOrderProductData.reduce((r, c) => r + c.vendorCost, 0) +
    regCart.cart.shippingTotalInCents;

  const cartServiceFees = regCart
    ? ecomOrderProductData.reduce((r, c) => r + c.serviceFees, 0) +
      regCart.cart.serviceFees.reduce(
        (r, c) => r + c.serviceFeeAmount + c.applicationFeeAmount,
        0
      )
    : 0;

  const invoiceServiceFees = invoices.reduce(
    (r, c) => r + c.serviceFeeInCents,
    0
  );

  const totalFees = regCart
    ? cartServiceFees +
      invoiceServiceFees +
      regCart.cart.tieredPricingFeeInCents
    : 0;

  const expectedVsRecordedServiceFeeDifference = regCart
    ? cartServiceFees -
      regFormSubmissionData.reduce(
        (r, c) => r + c.lineItem.platformFeeInCents,
        0
      ) -
      calculateExpectedProviderServiceFeeInCents(regCart.cart)
    : 0;

  const showServiceFeeDiscrepancy =
    expectedVsRecordedServiceFeeDifference !== 0 &&
    hasComparableServiceFees(regCart?.cart);

  const avaialbleBalance = regCart
    ? regCart.cart.amountPaidInCents +
      invoiceTotals.amountPaidInCents -
      vendorPayout -
      regCart.cart.refundedAmountInCents -
      invoiceTotals.refundedAmountInCents -
      totalFees +
      reimbursementAmount
    : 0;

  return (
    <PaymentSummaryInfoCard
      actionMenu={actionMenu}
      payments={[
        {
          key: 1,
          group: 1,
          label: 'Registrations',
          color: '#666',
          description: singularPluralFormat(
            regFormSubmissionData.reduce((r, c) => r + c.lineItem.quantity, 0),
            'item',
            'items'
          ),
          value:
            regFormSubmissionData.reduce(
              (r, c) => r + c.lineItem.subtotalInCents,
              0
            ) / 100
        },
        ...(ecomOrderProductData && ecomOrderProductData.length > 0
          ? [
              {
                key: 2,
                group: 1,
                label: 'Shop products',
                color: '#666',
                description: singularPluralFormat(
                  ecomOrderProductData.reduce((r, c) => r + c.quantity, 0),
                  'item',
                  'items'
                ),
                value:
                  ecomOrderProductData.reduce(
                    (r, c) => r + c.totalCost - c.taxTotal,
                    0
                  ) / 100
              }
            ]
          : []),
        ...(regCart.cart.discountTotalInCents > 0
          ? [
              {
                key: 3,
                group: 1,
                label: 'Discount',
                color: '#666',
                description: 'Discount details',
                value: (regCart.cart.discountTotalInCents / 100) * -1,
                children: [
                  ...regCart.cart.lineItems
                    .flatMap((i) =>
                      i.coupons.map((c) => ({
                        key: `${c.couponName}-${c.entityId}`,
                        label: c.couponName,
                        color: '#666',
                        value: c.amountSavedInCents / 100
                      }))
                    )
                    .reduce((r, c) => {
                      const exsiting = r.find((f) => f.label === c.label);
                      if (!exsiting) {
                        r.push(c);
                      }
                      else {
                        exsiting.value += c.value;
                      }
                      return r;
                    }, [])
                ]
              }
            ]
          : []),

        ...(showSubtotal
          ? [
              {
                key: 4,
                group: 2,
                label: 'Subtotal',
                weight: 500,
                description: '',
                value: regCart.cart.discountedSubtotalInCents / 100
              }
            ]
          : []),

        ...(regCart.cart.taxTotalInCents > 0
          ? [
              {
                key: 3,
                group: 1,
                label: 'Taxes',
                color: '#666',
                description: '',
                value: regCart.cart.taxTotalInCents / 100
              }
            ]
          : []),
        ...(regCart.cart.shippingTotalInCents > 0
          ? [
              {
                key: 6,
                group: showSubtotal ? 2 : 1,
                label: 'Shipping',
                color: '#666',
                description: 'Shipping details',
                value: regCart.cart.shippingTotalInCents / 100,
                children: regCart.cart.shippingCosts.map((t) => ({
                  key: t.ecomExternalConnectionExternalId,
                  label: t.label,
                  description: t.vendorName,
                  color: '#666',
                  value: t.totalInCents / 100
                }))
              }
            ]
          : []),
        ...(regCart.cart.customerServiceFeeTotalInCents > 0
          ? [
              {
                key: 7,
                group: showSubtotal ? 2 : 1,
                label: 'Service fee',
                color: '#666',
                description: '-',
                value: regCart.cart.customerServiceFeeTotalInCents / 100
              }
            ]
          : []),
        ...(regCart.cart.tieredPricingFeeInCents > 0
          ? [
              {
                key: 8,
                group: showSubtotal ? 2 : 1,
                label: 'Merch platform fee',
                color: '#666',
                description: '-',
                value: regCart.cart.tieredPricingFeeInCents / 100
              }
            ]
          : []),
        ...(invoices.length > 0
          ? [
              {
                key: 9,
                group: showSubtotal ? 2 : 1,
                label: 'Invoices',
                color: '#666',
                description: 'Invoice details',
                value: invoiceTotals.totalInCents / 100,
                children: invoices.map((i) => ({
                  key: i.entityId,
                  label: i.description,
                  color: '#666',
                  value: i.totalInCents / 100
                }))
              }
            ]
          : []),
        {
          key: 10,
          group: 3,
          label: 'Total',
          weight: 500,
          description: '',
          value: (regCart.cart.totalInCents + invoiceTotals.totalInCents) / 100
        },
        {
          key: 11,
          group: 3,
          label: 'Amount paid',
          color: '#067D62',
          description: '',
          value:
            (regCart.cart.amountPaidInCents + invoiceTotals.amountPaidInCents) /
            100
        },
        ...(pendingInstallments.length > 0
          ? [
              {
                key: 'amount-scheduled',
                group: 3,
                label: 'Amount scheduled',
                color: 'dodgerblue',
                value: (regCart.cart.installmentsScheduledInCents / 100) * -1,
                description: 'Installment details',
                children: pendingInstallments.map((i) => ({
                  key: i.entityId,
                  label: dayjs(formatUtcDate(i.dueDate)).format('MMM D, YYYY'),
                  color: '#666',
                  value: i.chargeAmount / 100
                }))
              }
            ]
          : []),
        ...(regCart.cart.amountDueInCents > 0 ||
        invoiceTotals.amountDueInCents > 0
          ? [
              {
                key: 8,
                group: 3,
                label: 'Amount due',
                color: '#C40000',
                value:
                  ((regCart.cart.amountDueInCents +
                    invoiceTotals.amountDueInCents) /
                    100) *
                  -1
              }
            ]
          : []),
        ...(vendorPayout > 0
          ? [
              {
                key: 8,
                group: 3,
                label: 'Vendor payout',
                color: '#666',
                value: (vendorPayout / 100) * -1
              }
            ]
          : []),
        {
          key: 9,
          group: 3,
          label: regCart.cart.taxTotalInCents > 0 ? 'Fees & taxes' : 'Fees',
          color: '#666',
          value: ((totalFees + regCart.cart.taxTotalInCents) / 100) * -1,
          tooltipIcon: <InfoCircle color="#C40000" size={18} />,
          tooltip: showServiceFeeDiscrepancy && (
            <AppStack style={{ gap: 8 }}>
              <AppText style={{ fontSize: 14 }}>
                Recorded service fee{' '}
                <b>
                  {currencyFormat(expectedVsRecordedServiceFeeDifference / 100)}{' '}
                  {expectedVsRecordedServiceFeeDifference < 0
                    ? 'lower'
                    : 'higher'}
                </b>{' '}
                than anticipated.
              </AppText>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Click for more info.
              </AppText>
            </AppStack>
          ),
          infoTitle: 'Service fee discrepancy',
          infoContent: showServiceFeeDiscrepancy ? (
            <AppStack style={{ gap: 8 }}>
              <AppText style={{ fontSize: 14 }}>
                Recorded service fee{' '}
                <b>
                  {currencyFormat(expectedVsRecordedServiceFeeDifference / 100)}{' '}
                  {expectedVsRecordedServiceFeeDifference < 0
                    ? 'lower'
                    : 'higher'}
                </b>{' '}
                than anticipated.
              </AppText>
              <AppText style={{ fontSize: 14 }}>
                This discrepancy may be due to additional service fees applied
                by your payment provider, such as a{' '}
                <b>+0.8% fee for international cards</b>.
              </AppText>
              <AppText style={{ fontSize: 14 }}>
                For more details, please visit{' '}
                <Anchor
                  component="a"
                  href="https://stripe.com/en-ca/pricing"
                  style={{ fontSize: 14, color: 'dodgerblue' }}
                  target="_blank"
                >
                  Stripe pricing
                </Anchor>{' '}
                for an example or contact support if you have any questions.
              </AppText>

              <AppText
                style={{
                  marginTop: 8,
                  fontSize: 14,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: '#666',
                  textAlign: 'center'
                }}
              >
                This variance will directly impact the store's profit margin.
              </AppText>
            </AppStack>
          ) : null
        },
        ...(reimbursementAmount > 0
          ? [
              {
                key: 11,
                group: 3,
                label: 'Reimbursed',
                prefix: reimbursementAmount > 0 ? '+$' : '$',
                color: '#666',
                valueColor: reimbursementAmount > 0 ? '#067D62' : '#666',
                description: '',
                value: reimbursementAmount / 100
              }
            ]
          : []),
        ...(regCart.cart.refundedAmountInCents > 0 ||
        invoiceTotals.refundedAmountInCents > 0
          ? [
              {
                key: 12,
                group: 3,
                label: 'Refunded',
                color: '#c40000',
                description: '',
                value:
                  ((regCart.cart.refundedAmountInCents +
                    invoiceTotals.refundedAmountInCents) *
                    -1) /
                  100
              }
            ]
          : []),
        {
          key: 11,
          group: 5,
          label: 'Net total',
          weight: 500,
          description: '',
          tooltipIcon: <InfoCircle color="dodgerblue" size={18} />,
          value: (avaialbleBalance - regCart.cart.taxTotalInCents) / 100
        }
      ]}
    />
  );
};

RegCartPaymentSummaryInfoCard.propTypes = {
  actionMenu: PropTypes.node,
  regCart: PropTypes.object,
  translatedOrder: PropTypes.object
};

export default RegCartPaymentSummaryInfoCard;
